import moment from 'moment';

import { ActionType, getType } from 'typesafe-actions';

import { DataCards } from './../../pages/AccountingCenter/store/types';
import * as dashboardActions from './actions';

type DashboardActionType = ActionType<typeof dashboardActions>;

export type DashboardState = {
  tradeValuationDate: string | null;
  tradeValuationDateLoading: boolean;
  totalValue: {
    totalPortfolioMtM: number;
    totalPortfolioMtMDate: string;
  };
  totalValueLoading: boolean;
  counterPartyValueLoading: boolean;
  counterPartyExposureLoading: boolean;
  counterPartyHedgeNotional: {
    counterparty_data: [];
    current_notional_total: number | undefined;
  };
  counterPartyExposure: {
    counterparty_data: [];
    value_date: string | undefined;
    value_total: number | undefined;
  };
  debtAndHedgeLoading: boolean;
  debtAndHedge: {
    debt_data: object;
    debt_data_date: string;
    debt_metrics: object;
  };
  hedgeTotalLoading: boolean;
  hedgeTotal: {
    notional_data: [];
    notional_metrics: object;
    notional_data_date: string;
  };
  totalPortfolioAndSparkDataLoading: boolean;
  totalPortfolioAndSparkData: dashboardActions.SetTotalPortfolioValueAndSparkDataResponse;
};

const initialState: DashboardState = {
  tradeValuationDate: moment().format('YYYY-MM-DD'),
  tradeValuationDateLoading: false,
  totalValue: {
    totalPortfolioMtM: 0,
    totalPortfolioMtMDate: '',
  },
  totalValueLoading: false,
  counterPartyValueLoading: false,
  counterPartyHedgeNotional: {
    counterparty_data: [],
    current_notional_total: undefined,
  },
  counterPartyExposureLoading: false,
  counterPartyExposure: {
    counterparty_data: [],
    value_date: '',
    value_total: undefined,
  },

  debtAndHedgeLoading: false,
  debtAndHedge: {
    debt_data: {},
    debt_data_date: '',
    debt_metrics: {},
  },

  hedgeTotalLoading: false,
  hedgeTotal: {
    notional_data: [],
    notional_metrics: {},
    notional_data_date: '',
  },
  totalPortfolioAndSparkDataLoading: false,
  totalPortfolioAndSparkData: {
    spark_data: [],
    current_value: null,
    current_date: '',
    previous_value: null,
    previous_date: '',
    change_amount: null,
    change_percentage: null,
  },
};

const dashboardReducer = (
  state: DashboardState = initialState,
  action: DashboardActionType,
): DashboardState => {
  switch (action.type) {
    case getType(dashboardActions.setClosestTradeValuationDate.request):
      return {
        ...state,
        tradeValuationDateLoading: true,
      };
    case getType(dashboardActions.setClosestTradeValuationDate.success): {
      return {
        ...state,
        tradeValuationDate: action.payload.date,
        tradeValuationDateLoading: false,
      };
    }
    case getType(dashboardActions.setClosestTradeValuationDate.failure):
      return {
        ...state,
        tradeValuationDateLoading: false,
      };

    case getType(dashboardActions.setTotalValuePortfolio.request):
      return {
        ...state,
        totalValueLoading: true,
      };
    case getType(dashboardActions.setTotalValuePortfolio.success): {
      return {
        ...state,
        totalValue: action.payload.data,
        totalValueLoading: false,
      };
    }
    case getType(dashboardActions.setTotalValuePortfolio.failure):
      return {
        ...state,
        totalValueLoading: false,
      };

    case getType(dashboardActions.setCounterPartyHedgedNotionals.request):
      return {
        ...state,
        counterPartyValueLoading: true,
        counterPartyHedgeNotional: {
          counterparty_data: [],
          current_notional_total: undefined,
        },
      };

    case getType(dashboardActions.setCounterPartyHedgedNotionals.success):
      return {
        ...state,
        counterPartyValueLoading: false,
        counterPartyHedgeNotional: action.payload.data,
      };

    case getType(dashboardActions.setCounterPartyHedgedNotionals.failure):
      return {
        ...state,
        counterPartyValueLoading: false,
      };

    case getType(dashboardActions.setCounterPartyExposure.request):
      return {
        ...state,
        counterPartyExposureLoading: true,
        counterPartyExposure: {
          counterparty_data: [],
          value_date: undefined,
          value_total: undefined,
        },
      };

    case getType(dashboardActions.setCounterPartyExposure.success):
      return {
        ...state,
        counterPartyExposureLoading: false,
        counterPartyExposure: action.payload.data,
      };

    case getType(dashboardActions.setCounterPartyExposure.failure): {
      return {
        ...state,
        counterPartyExposureLoading: false,
      };
    }

    case getType(dashboardActions.setDebtAndHedge.request):
      return {
        ...state,
        debtAndHedgeLoading: true,
        debtAndHedge: {
          debt_data: {},
          debt_data_date: '',
          debt_metrics: {},
        },
      };

    case getType(dashboardActions.setDebtAndHedge.success):
      return {
        ...state,
        debtAndHedgeLoading: false,
        debtAndHedge: action.payload.data,
      };

    case getType(dashboardActions.setDebtAndHedge.failure):
      return {
        ...state,
        debtAndHedgeLoading: false,
      };

    case getType(dashboardActions.setHedgeNotional.request):
      return {
        ...state,
        hedgeTotalLoading: true,
        hedgeTotal: {
          notional_data: [],
          notional_metrics: {},
          notional_data_date: '',
        },
      };

    case getType(dashboardActions.setHedgeNotional.success):
      return {
        ...state,
        hedgeTotalLoading: false,
        hedgeTotal: action.payload.data,
      };

    case getType(dashboardActions.setHedgeNotional.failure):
      return {
        ...state,
        hedgeTotalLoading: false,
      };
    case getType(dashboardActions.setTotalPortfolioValueAndSparkData.request):
      return {
        ...state,
        totalPortfolioAndSparkDataLoading: true,
      };
    case getType(dashboardActions.setTotalPortfolioValueAndSparkData.success): {
      return {
        ...state,
        totalPortfolioAndSparkData: action.payload,
        totalPortfolioAndSparkDataLoading: false,
      };
    }
    case getType(dashboardActions.setTotalPortfolioValueAndSparkData.failure):
      return {
        ...state,
        totalPortfolioAndSparkDataLoading: false,
      };
    default:
      return state;
  }
};

export default dashboardReducer;
